import type {
    ReactNode
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    NCoreUIKitTheme,
    type BadgeSize,
    Badge,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import {
    useDocsRouter
} from "../../router";
import {
    PageHeader,
    InlineCode
} from "../../components/content";
import {
    getComponentPath,
    findPage
} from "../../nav";

export type DocsLinkItem = {
    title: string;
    href: string;
};

const COMPONENT_PAGE_NAMES: Record<string, string> = {
    "Command.Dialog": "Command",
    "Badge.Indicator": "Badge",
    MenuButton: "Menu"
};

const stylesheet = StyleSheet.create({
    row: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    }
});

export const getComponentLink = (name: string): DocsLinkItem => ({
    href: getComponentPath(COMPONENT_PAGE_NAMES[name] ?? name),
    title: name
});

export const ReleaseNotesHeader = ({
    title,
    lead
}: {
    title: string;
    lead: string;
}) => {
    const {
        t
    } = useDocsI18n();

    return <PageHeader
        breadcrumbs={[
            {
                label: t("breadcrumb.docs"),
                href: "/"
            },
            {
                label: t("nav.sectionReleaseNotes")
            }
        ]}
        title={title}
        lead={lead}
    />;
};

export const LinkRow = ({
    size = "medium",
    items
}: {
    items: Array<DocsLinkItem>;
    size?: BadgeSize;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        getHref
    } = useDocsRouter();

    return <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        {
            items.map((item) => <Badge
                href={findPage(item.href) ? getHref(item.href) : undefined}
                title={item.title}
                variant="outline"
                key={item.title}
                type="neutral"
                size={size}
            />)
        }
    </View>;
};

export const ComponentLinks = ({
    names,
    size
}: {
    names: Array<string>;
    size?: BadgeSize;
}) => {
    return <LinkRow
        items={names.map(getComponentLink)}
        size={size}
    />;
};

export const CodeList = ({
    values
}: {
    values: Array<string>;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        {
            values.map((value) => <InlineCode
                key={value}
            >
                {value}
            </InlineCode>)
        }
    </View>;
};

export const TableCellContent = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={{
            paddingVertical: spaces.spacingSm
        }}
    >
        {children}
    </View>;
};

export const NoteCell = ({
    children
}: {
    children: string;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return <TableCellContent>
        <View
            style={{
                maxWidth: shapeConfigs.contentMaxWidth / 3
            }}
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {children}
            </Text>
        </View>
    </TableCellContent>;
};
